import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { MoreHorizontal, Loader2, Grid, StopCircle, Eye, Volume2, Zap } from 'lucide-react';
import { wktToGeoJSON } from '@terraformer/wkt';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FetchSubDevicesQuery, UpdateSecondaryDeviceMut } from '@/lib/api/devices/devices';
import { useUpdateZone } from '@/hooks/zoneHooks';
import { useSite } from '@/hooks/siteHooks';
import { updateSpeaker } from '@/lib/api/monitoring/speakers';
import { updateRelay } from '@/lib/api/monitoring/relays';
import { GridEditor } from '@/components/GridEditor';
import { DeviceSnapshot } from '@/components/device/DeviceSnapshot';
import { postSetCameraActivationZoneMonitoring } from '@/features/AdminAlert/alertApi';

interface CameraNodeData {
  deviceId: string;
  deviceName: string;
  assetName?: string;
  nvrName?: string;
  camera?: any;
  siteId?: string;
  companyId?: string;
  onStopMonitoring?: (deviceId: string) => void;
  onEditGrids?: (deviceId: string) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  hoveredCameraId?: string | null;
  onShowPopout?: (data: { deviceId: string; deviceName: string; nvrId?: string; activationWkt?: string; position: { x: number; y: number } }) => void;
  onHidePopout?: () => void;
  onCameraClick?: () => void;
  isLinkingMode?: boolean;
  isLinkedInMode?: boolean;
  onDeviceDrop?: (deviceData: any, cameraId: string) => void;
  totalCameraCount?: number;
  allSpeakers?: any[];
  allRelays?: any[];
}

interface CameraNodeProps {
  id: string;
  data: CameraNodeData;
  selected?: boolean;
}

export const CameraNode: React.FC<CameraNodeProps> = memo(({ data, selected }) => {
  const totalCameras = useMemo(() => data.totalCameraCount || 0, [data.totalCameraCount]);
  const [showPopout, setShowPopout] = useState(false);
  const [shouldFetchSecondaryDevice, setShouldFetchSecondaryDevice] = useState(false);
  const [gridEditorOpen, setGridEditorOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isOnLeftSide, setIsOnLeftSide] = useState(false);

  const queryClient = useQueryClient();
  const { data: site } = useSite({ siteId: data.siteId || '', companyId: data.companyId || '' });
  const { mutate: updateZoneMutation } = useUpdateZone({ projectId: data.siteId || '', companyId: data.companyId || '' });
  const { getZoom } = useReactFlow();

  // Get the NVR ID from the devices[].nvrId structure
  const nvrId = data.camera?.nvrId;

  // Use existing FetchSubDevicesQuery to get secondary device data with activation WKT
  const { data: secondaryDevices, isLoading: secondaryDeviceLoading } = useQuery({
    ...FetchSubDevicesQuery({ deviceId: nvrId }),
    enabled: Boolean(shouldFetchSecondaryDevice && nvrId),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Secondary devices fetched for activation WKT

  
  // Find the specific secondary device that matches this camera's ID
  const secondaryDevice = secondaryDevices?.find(device => device.id === data.deviceId);

  
  // Determine final activation WKT (prioritize API data since it's more complete)
  const localActivationWkt = data.camera?.activationWkt;
  const apiActivationWkt = secondaryDevice?.activationWkt;
  const finalActivationWkt = apiActivationWkt || localActivationWkt;


  // Mutation for updating secondary device monitoring status
  const updateSecondaryDeviceMut = useMutation({
    ...UpdateSecondaryDeviceMut(nvrId || ''),
    onSuccess: () => {
      // Use the same query invalidation pattern as drop handlers
      if (data.siteId && data.companyId) {
        queryClient.refetchQueries({ 
          queryKey: ['siteDevices', { companyId: data.companyId, projectId: data.siteId }]
        });
      }
      // Also invalidate the secondary devices query
      queryClient.invalidateQueries({ queryKey: ['device.secondary', nvrId] });
    },
  });

  const handleStopMonitoring = async () => {
    if (!nvrId || !secondaryDevices || secondaryDevices.length === 0) {
      return;
    }
    
    // 1. Update the device monitoring status
    const updatedDevices = secondaryDevices.map(device => {
      if (device.id === data.deviceId) {
        // Update only the target camera to monitored: false
        return {
          id: device.id,
          monitored: false,
          description: device.description,
          quillNotes: device.quillNotes,
          capabilities: device.capabilities,
        };
      } else {
        // Preserve existing monitored state for all other cameras
        return {
          id: device.id,
          monitored: device.monitored, // Keep existing monitored state
          description: device.description,
          quillNotes: device.quillNotes,
          capabilities: device.capabilities,
        };
      }
    });

    updateSecondaryDeviceMut.mutate({
      primaryDeviceId: nvrId,
      devices: updatedDevices
    }, {
      onSuccess: () => {
        // Force invalidate all site-related queries immediately
        if (data.siteId && data.companyId) {
          queryClient.invalidateQueries({ 
            queryKey: ['siteDevices', { companyId: data.companyId, projectId: data.siteId }],
            refetchType: 'all'
          });
          queryClient.invalidateQueries({ 
            queryKey: ['site', data.siteId, data.companyId],
            refetchType: 'all'
          });
        }
      }
    });
    
    // 2. Remove camera from zone if it's assigned to one
    if (site?.monitoringZones && data.camera) {
      const assignedZone = site.monitoringZones.find(zone => 
        zone.videoProcessors?.some(vp => vp.videoProcessorConfigId === data.deviceId)
      );
      
      if (assignedZone && assignedZone.id) {
        const updatedVideoProcessorIds = assignedZone.videoProcessors
          ?.filter(vp => vp.videoProcessorConfigId !== data.deviceId)
          .map(vp => vp.videoProcessorConfigId)
          .filter(Boolean) as string[] || [];
        
        updateZoneMutation({
          zoneId: assignedZone.id,
          form: {
            description: assignedZone.description,
            videoProcessorIds: updatedVideoProcessorIds,
            schedule: assignedZone.schedule
          }
        });
      }
    }
    
    // 3. Remove camera from linked speakers and relays
    const originalDeviceId = data.camera?.originalDevice?.id;
    if (originalDeviceId && data.siteId && data.companyId) {
      // Get all speakers and relays from the processed device data
      // Need to get the raw processed data from useDeviceData hook
      
      // Get speakers and relays from the parent component context
      // For now, let's use a simpler approach and get them via import
      try {
        const { speakers: allSpeakers, relays: allRelays } = await (async () => {
          // Re-fetch and process the site devices to get current speakers/relays
          const siteDevicesKey = ['siteDevices', { companyId: data.companyId, projectId: data.siteId }];
          const siteDevicesData = queryClient.getQueryData(siteDevicesKey) as any;
          
          if (!siteDevicesData?.devices) return { speakers: [], relays: [] };
          
          const speakers: any[] = [];
          const relays: any[] = [];
          
          siteDevicesData.devices.forEach((device: any, index: number) => {
            if (!device.secondaryDevice) return;
            const deviceName = device.secondaryDevice.name || `Device ${index + 1}`;
            const integrationType = device.secondaryDevice.integrationType || 'Nvr';
            
            if (integrationType === 'Speaker' && device.secondaryDevice.speakers?.[0]) {
              const speaker = device.secondaryDevice.speakers[0];
              speakers.push({
                id: speaker.id,
                name: speaker.name || deviceName,
                relatedComponentGroups: speaker.relatedComponentGroups || [],
                componentGroupId: speaker.componentGroupId || device.secondaryDevice.id,
                enabled: device.secondaryDevice.monitored
              });
            } else if (integrationType === 'Relay' && device.secondaryDevice.relays?.[0]) {
              const relay = device.secondaryDevice.relays[0];
              relays.push({
                id: relay.id,
                name: relay.name || deviceName,
                relatedComponentGroups: relay.relatedComponentGroups || [],
                componentGroupId: relay.componentGroupId || device.secondaryDevice.id,
                enabled: device.secondaryDevice.monitored
              });
            }
          });
          
          return { speakers, relays };
        })();
        
        
        // Update speakers that have this camera linked
        const linkedSpeakers = allSpeakers.filter((speaker: any) => 
          speaker.relatedComponentGroups?.includes(originalDeviceId)
        );
        
        for (const speaker of linkedSpeakers) {
          const updatedComponentGroups = speaker.relatedComponentGroups.filter(
            (id: string) => id !== originalDeviceId
          );
          
          await updateSpeaker({
            id: speaker.id,
            name: speaker.name,
            componentGroupId: speaker.componentGroupId || speaker.id,
            relatedComponentGroups: updatedComponentGroups,
            enabled: speaker.enabled !== false
          });
        }
        
        // Update relays that have this camera linked  
        const linkedRelays = allRelays.filter((relay: any) => 
          relay.relatedComponentGroups?.includes(originalDeviceId)
        );
        
        
        for (const relay of linkedRelays) {
          const updatedComponentGroups = relay.relatedComponentGroups.filter(
            (id: string) => id !== originalDeviceId
          );
          
          await updateRelay({
            id: relay.id,
            name: relay.name,
            componentGroupId: relay.componentGroupId || relay.id,
            relatedComponentGroups: updatedComponentGroups,
            enabled: relay.enabled !== false
          });
        }
        
      } catch (speakerRelayError) {
        console.error('🛑 Error unlinking speakers/relays:', speakerRelayError);
      }
    }
    
    if (data.onStopMonitoring) {
      data.onStopMonitoring(data.deviceId);
    }
  };

  const handleEditGrids = () => {
    setShowPopout(false); // Hide popover when opening grid editor
    setGridEditorOpen(true);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    // Determine which side of the screen the camera is on
    const rect = e.currentTarget.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const isLeftSide = rect.left < screenWidth / 2;
    
    setIsOnLeftSide(isLeftSide);
    setShowPopout(true);
    
    // If we don't have local activation WKT, trigger secondary device fetch
    if (!localActivationWkt && nvrId) {
      setShouldFetchSecondaryDevice(true);
    }
    
    if (data.onMouseEnter) {
      data.onMouseEnter();
    }
  };

  const handleMouseLeave = () => {
    setShowPopout(false);
    setShouldFetchSecondaryDevice(false);
    
    // Notify parent to hide popout
    if (data.onHidePopout) {
      data.onHidePopout();
    }
    
    if (data.onMouseLeave) {
      data.onMouseLeave();
    }
  };

  const isHovered = data.hoveredCameraId === data.deviceId;

  const handleCameraClick = (e: React.MouseEvent) => {
    
    if (data.isLinkingMode && data.onCameraClick) {
      e.preventDefault();
      e.stopPropagation();
      data.onCameraClick();
      return;
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent zone drop handler from firing
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent zone drop handler from firing
    setIsDragOver(false);

    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));

      if (data.onDeviceDrop && (dragData.deviceType === 'speaker' || dragData.deviceType === 'relay')) {
        data.onDeviceDrop(dragData, data.deviceId);
      } else {
        // Invalid drop or no handler
      }
    } catch (error) {
      console.error('📷 CAMERA DROP - Failed to parse drop data', error);
    }
  };

  return (
    <div 
      className={`border rounded-lg shadow-lg p-3 min-w-[150px] transition-all duration-200 ${
        selected ? 'ring-2 ring-blue-400 ring-opacity-75' : ''
      } ${
        data.isLinkingMode ? 'cursor-pointer hover:bg-gray-800' : ''
      } ${
        data.isLinkedInMode ? 'border-2 shadow-green-400/20' : ''
      } ${
        isDragOver ? 'border-2 border-blue-400 bg-blue-900/20 shadow-blue-400/20' : ''
      }`}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        ...(data.isLinkedInMode && { borderColor: '#d1f551' })
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleCameraClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Camera Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div 
            className="text-sm font-semibold text-white truncate max-w-[120px]"
            title={data.deviceName}
          >
            {data.deviceName}
          </div>
          {data.nvrName && (
            <div 
              className="text-xs text-gray-400 truncate max-w-[120px]"
              title={data.nvrName}
            >
              {data.nvrName}
            </div>
          )}
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="xs"
              className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-600"
              disabled={data.isLinkingMode}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem 
              onClick={() => {
                if (nvrId) {
                  window.location.href = `/devices/${nvrId}/edit`;
                }
              }} 
              className="cursor-pointer"
              disabled={!nvrId}
            >
              <Eye className="h-4 w-4 mr-2" />
              View Device
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleEditGrids} className="cursor-pointer">
              <Grid className="h-4 w-4 mr-2" />
              Edit Grid
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleStopMonitoring} 
              className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300"
            >
              <StopCircle className="h-4 w-4 mr-2" />
              Stop Monitoring
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Camera Preview - Show preview if ≤20 cameras, otherwise show hover placeholder */}
      {totalCameras <= 20 ? (
        <div className="w-full h-20 overflow-hidden rounded border border-gray-600 bg-gray-800">
          <DeviceSnapshot
            deviceId={data.deviceId}
            className="w-full h-full object-cover"
            withLoadingText={false}
          />
        </div>
      ) : (
        <div className="w-full h-20 flex items-center justify-center rounded border border-gray-600 bg-gray-800">
          <div className="flex flex-col items-center space-y-1 text-gray-400">
            <Eye className="h-5 w-5" />
            <span className="text-xs font-medium">Hover to preview</span>
          </div>
        </div>
      )}

      {/* React Flow Handles - Hidden but functional */}
      <Handle
        id="top"
        type="source"
        position={Position.Top}
        isConnectable={true}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
      <Handle
        id="bottom"
        type="source"
        position={Position.Bottom}
        isConnectable={true}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />

      {/* Camera Hover Popout using Portal to fixed container */}
      {showPopout && (() => {
        // Choose container based on which side of screen the camera is on
        // If camera is on left side, show preview on right side to avoid overlap
        const containerId = isOnLeftSide ? 'camera-hover-card-right' : 'camera-hover-card-left';
        const targetContainer = document.getElementById(containerId);
        return targetContainer ? createPortal(
          <div className="rounded-lg p-4 pointer-events-none w-full h-full border" style={{ backgroundColor: '#17221f' }}>
            <div className="flex flex-col space-y-3 h-full">
              <div className="flex items-center justify-between">
                <p className="text-white text-lg font-semibold truncate">
                  {data.deviceName}
                </p>
                {secondaryDeviceLoading && (
                  <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
                )}
              </div>
              
              {data.nvrName && (
                <p className="text-gray-400 text-sm">
                  {data.nvrName}
                </p>
              )}
              {finalActivationWkt ? (
                <div className="w-24 text-xs px-2 py-1 rounded text-center" style={{ backgroundColor: 'rgba(188, 215, 82, 0.8)', color: '#000' }}>
                  Detection Grid
                </div>
              ) : (
                <div className="w-20 text-xs px-2 py-1 rounded bg-yellow-500/30 border border-yellow-500 text-yellow-200 text-center">
                  No Grid
                </div>
              )}
              {/* Camera Preview - Takes up remaining space */}
              <div className="relative w-full flex-1 rounded border border-gray-600 bg-gray-800 overflow-hidden">
                <DeviceSnapshot
                  deviceId={data.deviceId}
                  className="w-full h-full object-contain"
                  withLoadingText={false}
                />
                
                {/* WKT Polygon Overlay - positioned exactly over the image */}
                {finalActivationWkt && (
                  <SimpleWKTOverlay wkt={finalActivationWkt} />
                )}
                
                {/* Loading Overlay for Grid */}
                {secondaryDeviceLoading && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="flex flex-col items-center space-y-2">
                      <Loader2 className="h-12 w-12 text-blue-400 animate-spin" />
                      <p className="text-white text-sm">Loading detection grid...</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Connected Devices List */}
              {(() => {
                const connectedSpeakers = data.allSpeakers?.filter((speaker: any) => 
                  speaker.relatedComponentGroups?.includes(data.camera?.originalDevice?.id)
                ) || [];
                
                const connectedRelays = data.allRelays?.filter((relay: any) => 
                  relay.relatedComponentGroups?.includes(data.camera?.originalDevice?.id)
                ) || [];
                
                const hasConnections = connectedSpeakers.length > 0 || connectedRelays.length > 0;
                
                if (!hasConnections) return null;
                
                return (
                  <div className="mt-3 pt-2 border-t border-gray-600">
                    <div className="text-xs text-gray-400 mb-2">Connected Components:</div>
                    
                    {connectedSpeakers.length > 0 && (
                      <div className="flex items-center gap-1 mb-1">
                        <Volume2 className="h-3 w-3 text-green-400" />
                        <div className="text-xs text-green-300">
                          {connectedSpeakers.map((speaker: any, idx: number) => (
                            <span key={speaker.id}>
                              {speaker.name || speaker.assetName || 'Speaker'}
                              {idx < connectedSpeakers.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {connectedRelays.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-yellow-400" />
                        <div className="text-xs text-yellow-300">
                          {connectedRelays.map((relay: any, idx: number) => (
                            <span key={relay.id}>
                              {relay.name || relay.assetName || 'Relay'}
                              {idx < connectedRelays.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>,
          targetContainer
        ) : null;
      })()}

      {/* Edit Grid Dialog */}
      <Dialog open={gridEditorOpen} onOpenChange={setGridEditorOpen}>
        <DialogContent className="max-w-[50vw] max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Edit Detection Grid - {data.deviceName}</DialogTitle>
          </DialogHeader>
          
          <GridEditor
              deviceId={data.deviceId}
              deviceName={data.deviceName}
              initialWkt={finalActivationWkt}
              videoProcessorId={secondaryDevice?.datalayerVpc}
              onSave={async (polygons: string[]) => {
              try {
                // Save the polygon data via API
                if (secondaryDevice?.datalayerVpc) {
                  
                  if(polygons.length === 0){
                    const result = await postSetCameraActivationZoneMonitoring(
                      secondaryDevice.datalayerVpc,
                      { wkt: "" }
                    );
                    
                  }
                  else if (polygons.length === 1) {
                    // Single polygon
                    const result = await postSetCameraActivationZoneMonitoring(
                      secondaryDevice.datalayerVpc,
                      { wkt: polygons[0] }
                    );
                  } else {
                    // Multiple polygons - extract coordinates and combine properly
                    const polygonCoords = polygons.map(polygon => {
                      // Remove MULTIPOLYGON/POLYGON wrapper and extract just the coordinates
                      if (polygon.includes('MULTIPOLYGON')) {
                        // Extract coordinates from MULTIPOLYGON (((coords)))
                        const match = polygon.match(/MULTIPOLYGON \(\(\((.*?)\)\)\)/);
                        return match ? `((${match[1]}))` : polygon;
                      } else if (polygon.includes('POLYGON')) {
                        // Extract coordinates from POLYGON ((coords))
                        const match = polygon.match(/POLYGON \((.*?)\)/);
                        return match ? `(${match[1]})` : polygon;
                      }
                      return `((${polygon}))`;
                    });
                    
                    const multiPolygonWkt = `MULTIPOLYGON(${polygonCoords.join(',')})`;
                    
                    const result = await postSetCameraActivationZoneMonitoring(
                      secondaryDevice.datalayerVpc,
                      { wkt: multiPolygonWkt }
                    );
                  }
                }
                
                setGridEditorOpen(false);
                // Refetch secondary devices to get updated WKT
                queryClient.invalidateQueries({ queryKey: ['device.secondary', nvrId] });
              } catch (error) {
                console.error('💾 Grid save error:', error);
              }
            }}
            onCancel={() => {
              setGridEditorOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
});

CameraNode.displayName = 'CameraNode';

// Component to render WKT polygon overlay with responsive dimensions
interface WKTOverlayResponsiveProps {
  wkt: string;
}

export const WKTOverlayResponsive: React.FC<WKTOverlayResponsiveProps> = ({ wkt }) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const [imageDimensions, setImageDimensions] = useState({ naturalWidth: 1920, naturalHeight: 1080 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        // Find the actual image element and its container
        const imageElement = containerRef.current.parentElement?.querySelector('img');
        if (imageElement) {
          // Get the image element's container (which might be constrained)
          const containerRect = containerRef.current.getBoundingClientRect();
          const naturalWidth = imageElement.naturalWidth || 1920;
          const naturalHeight = imageElement.naturalHeight || 1080;
          
          // Calculate the actual displayed image size within the container (accounting for object-contain)
          const containerAspect = containerRect.width / containerRect.height;
          const imageAspect = naturalWidth / naturalHeight;
          
          let displayedWidth, displayedHeight, offsetX, offsetY;
          
          if (imageAspect > containerAspect) {
            // Image is wider - letterboxed on top/bottom
            displayedWidth = containerRect.width;
            displayedHeight = containerRect.width / imageAspect;
            offsetX = 0;
            offsetY = (containerRect.height - displayedHeight) / 2;
          } else {
            // Image is taller - letterboxed on left/right  
            displayedWidth = containerRect.height * imageAspect;
            displayedHeight = containerRect.height;
            offsetX = (containerRect.width - displayedWidth) / 2;
            offsetY = 0;
          }
          
          setDimensions({ 
            width: displayedWidth, 
            height: displayedHeight,
            offsetX,
            offsetY
          });
          setImageDimensions({ 
            naturalWidth,
            naturalHeight
          });
        } else {
          // Fallback to container dimensions
          const rect = containerRef.current.getBoundingClientRect();
          setDimensions({ width: rect.width, height: rect.height, offsetX: 0, offsetY: 0 });
        }
      }
    };

    // Initial measurement with delay to ensure Portal rendering
    const timeoutId = setTimeout(() => {
      updateDimensions();
    }, 150);

    // Set up ResizeObserver for more accurate dimension tracking
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(containerRef.current);
    }

    // Fallback to window resize listener
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateDimensions);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [wkt]); // Re-run when WKT changes

  if (dimensions.width === 0 || dimensions.height === 0) {
    return <div ref={containerRef} className="absolute inset-0" />;
  }

  return (
    <div ref={containerRef} className="absolute top-0 left-0 w-full h-full pointer-events-none">
      <div 
        className="absolute"
        style={{
          left: `${dimensions.offsetX}px`,
          top: `${dimensions.offsetY}px`,
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`
        }}
      >
        <WKTOverlay 
          wkt={wkt}
          imageWidth={dimensions.width}
          imageHeight={dimensions.height}
          naturalWidth={imageDimensions.naturalWidth}
          naturalHeight={imageDimensions.naturalHeight}
        />
      </div>
    </div>
  );
};

// Component to render WKT polygon overlay
interface WKTOverlayProps {
  wkt: string;
  imageWidth: number;
  imageHeight: number;
  naturalWidth: number;
  naturalHeight: number;
}

const WKTOverlay: React.FC<WKTOverlayProps> = ({ wkt, imageWidth, imageHeight, naturalWidth, naturalHeight }) => {
  // Parse WKT using proven terraformer library (same as GridEditor)
  const parseWKTCoordinates = (wktString: string) => {
    if (!wktString.trim()) {
      return null;
    }

    try {
      const geojson = wktToGeoJSON(wktString);
      
      if (geojson.type === "MultiPolygon") {
        // Return ALL polygons from MultiPolygon
        return geojson.coordinates.map(polygon => {
          if (polygon && polygon[0]) {
            // Return the outer ring coordinates for each polygon
            return polygon[0].map(([x, y]) => ({ x, y }));
          }
          return null;
        }).filter(Boolean);
      } else if (geojson.type === "Polygon") {
        // Single polygon - return as array with one polygon
        const outerRing = geojson.coordinates[0];
        if (outerRing) {
          return [outerRing.map(([x, y]) => ({ x, y }))];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to parse WKT:', error);
      return null;
    }
  };

  const coordinatesArray = parseWKTCoordinates(wkt);
  
  if (!coordinatesArray || coordinatesArray.length === 0) {
    return null;
  }

  // Scale WKT coordinates from natural image size to container size
  const scaleX = imageWidth / naturalWidth;
  const scaleY = imageHeight / naturalHeight;

  return (
    <svg 
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${imageWidth} ${imageHeight}`}
      preserveAspectRatio="none"
    >
      {coordinatesArray.map((coordinates, index) => {
        if (!coordinates) return null;
        // Convert coordinates to scaled SVG coordinates for this polygon
        const svgPoints = coordinates.map(coord => {
          // Scale the coordinates to fit the container
          const x = (coord?.x ?? 0) * scaleX;
          const y = (coord?.y ?? 0) * scaleY;
          
          // Validate the converted coordinates
          if (isNaN(x) || isNaN(y)) {
            return null;
          }
          
          return `${x},${y}`;
        }).filter(Boolean).join(' ');
        
        if (!svgPoints) {
          return null;
        }

        return (
          <polygon
            key={index}
            points={svgPoints}
            fill="rgba(59, 130, 246, 0.2)"
            stroke="#3b82f6"
            strokeWidth="2"
            fillRule="evenodd"
          />
        );
      })}
    </svg>
  );
};

// Simple WKT overlay that renders directly in the image container with object-contain support
interface SimpleWKTOverlayProps {
  wkt: string;
}

const SimpleWKTOverlay: React.FC<SimpleWKTOverlayProps> = ({ wkt }) => {
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const imageElement = container.parentElement?.querySelector('img');
        
        if (imageElement && imageElement.complete) {
          const containerRect = container.getBoundingClientRect();
          const naturalWidth = imageElement.naturalWidth || 1920;
          const naturalHeight = imageElement.naturalHeight || 1080;
          
          // Calculate actual displayed image dimensions with object-contain
          const containerAspect = containerRect.width / containerRect.height;
          const imageAspect = naturalWidth / naturalHeight;
          
          let displayedWidth, displayedHeight, offsetX, offsetY;
          
          if (imageAspect > containerAspect) {
            // Image is wider - letterboxed on top/bottom
            displayedWidth = containerRect.width;
            displayedHeight = containerRect.width / imageAspect;
            offsetX = 0;
            offsetY = (containerRect.height - displayedHeight) / 2;
          } else {
            // Image is taller - letterboxed on left/right
            displayedWidth = containerRect.height * imageAspect;
            displayedHeight = containerRect.height;
            offsetX = (containerRect.width - displayedWidth) / 2;
            offsetY = 0;
          }
          
          setImageDimensions({
            width: displayedWidth,
            height: displayedHeight,
            offsetX,
            offsetY
          });
        }
      }
    };

    const timer = setTimeout(updateDimensions, 100);
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
    };
  }, [wkt]);

  // Parse WKT using proven terraformer library (same as GridEditor)
  const parseWKTCoordinates = (wktString: string) => {
    if (!wktString.trim()) {
      return null;
    }

    try {
      const geojson = wktToGeoJSON(wktString);
      
      if (geojson.type === "MultiPolygon") {
        return geojson.coordinates.map(polygon => {
          if (polygon && polygon[0]) {
            return polygon[0].map(([x, y]) => ({ x, y }));
          }
          return null;
        }).filter(Boolean);
      } else if (geojson.type === "Polygon") {
        const outerRing = geojson.coordinates[0];
        if (outerRing) {
          return [outerRing.map(([x, y]) => ({ x, y }))];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to parse WKT:', error);
      return null;
    }
  };

  const coordinatesArray = parseWKTCoordinates(wkt);
  
  if (!coordinatesArray || coordinatesArray.length === 0 || imageDimensions.width === 0) {
    return <div ref={containerRef} className="absolute inset-0 pointer-events-none" />;
  }

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none">
      <svg 
        className="absolute pointer-events-none"
        style={{
          left: `${imageDimensions.offsetX}px`,
          top: `${imageDimensions.offsetY}px`,
          width: `${imageDimensions.width}px`,
          height: `${imageDimensions.height}px`
        }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {coordinatesArray.map((coordinates, index) => {
          if (!coordinates) return null;
          const svgPoints = coordinates.map(coord => {
            const x = (coord?.x ?? 0) * 100;
            const y = (coord?.y ?? 0) * 100;
            return `${x},${y}`;
          }).join(' ');

          return (
            <polygon
              key={index}
              points={svgPoints}
              fill="rgba(188, 215, 82, 0.3)"
              stroke="#BCD752"
              strokeWidth="0.5"
              fillRule="evenodd"
            />
          );
        })}
      </svg>
    </div>
  );
};
